import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { User, Building2, Mail, Phone, Search, Plus, Users } from 'lucide-react';
import MonthlyReportButton from '@/components/MonthlyReportButton';
import NewClientDialog from '@/components/NewClientDialog';
import MergeClientsDialog from '@/components/MergeClientsDialog';

const TYPE_TABS = [
  { id: 'all', label: 'Todos' },
  { id: 'PF', label: 'Pessoa Física' },
  { id: 'PJ', label: 'Pessoa Jurídica' },
];

function Clients({ user }) {
  const [clients, setClients] = useState([]);
  const [projectsByClient, setProjectsByClient] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const canManage = ['ADMIN', 'CONSULTANT'].includes((user?.role || '').toUpperCase());

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [clientsRes, projectsRes] = await Promise.all([
        api.get('/clients'),
        api.get('/projects'),
      ]);
      setClients(clientsRes.data || []);
      const byClient = {};
      (projectsRes.data || []).forEach(p => {
        if (!p.client_id) return;
        byClient[p.client_id] = byClient[p.client_id] || [];
        byClient[p.client_id].push(p);
      });
      setProjectsByClient(byClient);
    } catch (err) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter(c => {
      if (activeTab !== 'all' && c.person_type !== activeTab) return false;
      if (!q) return true;
      const haystack = [
        c.full_name, c.email, c.company || c.company_name,
        c.contact_person, c.phone,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [clients, activeTab, search]);

  const counts = useMemo(() => ({
    all: clients.length,
    PF: clients.filter(c => c.person_type === 'PF').length,
    PJ: clients.filter(c => c.person_type === 'PJ').length,
  }), [clients]);

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
  };

  const openEdit = (client) => {
    setEditForm({
      user_id: client.user_id,
      full_name: client.full_name || '',
      email: client.email || '',
      phone: client.phone || '',
      company_name: client.company_name || client.company || '',
      contact_person: client.contact_person || '',
      person_type: client.person_type || 'PF',
      notes: client.notes || '',
    });
  };

  const saveEdit = async () => {
    if (!editForm.full_name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: editForm.full_name,
        email: editForm.email || null,
        phone: editForm.phone || null,
        company_name: editForm.company_name || null,
        contact_person: editForm.contact_person || null,
        person_type: editForm.person_type,
        notes: editForm.notes || null,
      };
      await api.put(`/clients/${editForm.user_id}`, payload);
      toast.success('Cliente atualizado');
      setEditForm(null);
      setSelected(null);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Clientes</h1>
            <p className="text-gray-600 mt-1">Cadastro unificado — clientes criados via Projetos ou Orçamentos aprovados aparecem aqui automaticamente</p>
          </div>
          {canManage && (
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={() => setMergeOpen(true)}
                data-testid="btn-review-duplicates"
              >
                <Users className="w-4 h-4 mr-1" />
                Revisar Duplicados
              </Button>
              <Button
                onClick={() => setNewClientOpen(true)}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="btn-new-client"
              >
                <Plus className="w-4 h-4 mr-1" />
                Novo Cliente
              </Button>
            </div>
          )}
        </div>

        {/* Type tabs + search */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2 flex-wrap">
            {TYPE_TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors ' +
                  (activeTab === tab.id
                    ? 'bg-emerald-700 text-white'
                    : 'bg-white border text-gray-700 hover:bg-gray-50')
                }
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
                <span
                  className={
                    'ml-2 text-[11px] font-bold px-1.5 py-0.5 rounded ' +
                    (activeTab === tab.id ? 'bg-emerald-900 text-white' : 'bg-gray-100 text-gray-700')
                  }
                >
                  {counts[tab.id]}
                </span>
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Buscar por nome, email, empresa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="clients-search-input"
            />
          </div>
        </div>

        {/* Cards grid — each client is a clickable button */}
        {filtered.length === 0 ? (
          <Card className="p-10 text-center text-gray-500" data-testid="clients-empty">
            {search.trim() ? 'Nenhum cliente encontrado para a busca' : 'Nenhum cliente cadastrado'}
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map(client => {
              const isPJ = client.person_type === 'PJ';
              const projects = projectsByClient[client.user_id] || [];
              return (
                <button
                  key={client.user_id}
                  type="button"
                  onClick={() => setSelected(client)}
                  className={
                    'group text-left p-4 rounded-xl border-2 transition-all hover:shadow-md ' +
                    (isPJ
                      ? 'bg-blue-50 border-blue-200 hover:border-blue-400'
                      : 'bg-emerald-50 border-emerald-200 hover:border-emerald-400')
                  }
                  data-testid={`client-card-${client.user_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className={
                        'w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ' +
                        (isPJ ? 'bg-blue-600' : 'bg-emerald-600')
                      }
                    >
                      {isPJ ? <Building2 className="w-6 h-6" /> : <span>{getInitials(client.full_name)}</span>}
                    </div>
                    <Badge
                      className={
                        'text-[10px] ' +
                        (isPJ ? 'bg-blue-200 text-blue-900' : 'bg-emerald-200 text-emerald-900')
                      }
                      data-testid={`client-type-${client.user_id}`}
                    >
                      {isPJ ? 'PJ' : 'PF'}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-gray-900 leading-tight line-clamp-2">
                    {client.full_name || '(sem nome)'}
                  </h3>
                  {isPJ && (client.contact_person) && (
                    <p className="text-[11px] text-gray-600 mt-0.5 truncate">
                      <User className="w-3 h-3 inline mr-1" />
                      {client.contact_person}
                    </p>
                  )}
                  <div className="mt-2 space-y-0.5 text-xs text-gray-700">
                    {client.email && (
                      <p className="truncate"><Mail className="w-3 h-3 inline mr-1 text-gray-400" />{client.email}</p>
                    )}
                    {client.phone && (
                      <p className="truncate"><Phone className="w-3 h-3 inline mr-1 text-gray-400" />{client.phone}</p>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200/60 flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      {projects.length} projeto{projects.length !== 1 ? 's' : ''}
                    </span>
                    <span className={(isPJ ? 'text-blue-700' : 'text-emerald-700') + ' font-medium opacity-0 group-hover:opacity-100 transition'}>
                      Ver detalhes →
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Detail modal */}
        <Dialog open={!!selected && !editForm} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="max-w-2xl">
            {selected && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3">
                    <div className={
                      'w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg ' +
                      (selected.person_type === 'PJ' ? 'bg-blue-600' : 'bg-emerald-600')
                    }>
                      {selected.person_type === 'PJ'
                        ? <Building2 className="w-7 h-7" />
                        : <span>{getInitials(selected.full_name)}</span>}
                    </div>
                    <div>
                      <DialogTitle>{selected.full_name || '(sem nome)'}</DialogTitle>
                      <DialogDescription className="sr-only">
                        Detalhes do cliente {selected.full_name || ''}
                      </DialogDescription>
                      <Badge className={
                        selected.person_type === 'PJ'
                          ? 'bg-blue-200 text-blue-900 mt-1'
                          : 'bg-emerald-200 text-emerald-900 mt-1'
                      }>
                        {selected.person_type === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                      </Badge>
                    </div>
                  </div>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                  {selected.email && (
                    <div>
                      <Label className="text-xs text-gray-500">E-mail</Label>
                      <p className="text-sm text-gray-900">{selected.email}</p>
                    </div>
                  )}
                  {selected.phone && (
                    <div>
                      <Label className="text-xs text-gray-500">Telefone</Label>
                      <p className="text-sm text-gray-900">{selected.phone}</p>
                    </div>
                  )}
                  {(selected.company || selected.company_name) && (
                    <div>
                      <Label className="text-xs text-gray-500">Empresa</Label>
                      <p className="text-sm text-gray-900">{selected.company || selected.company_name}</p>
                    </div>
                  )}
                  {selected.contact_person && (
                    <div>
                      <Label className="text-xs text-gray-500">Pessoa de Contato</Label>
                      <p className="text-sm text-gray-900">{selected.contact_person}</p>
                    </div>
                  )}
                  {selected.notes && (
                    <div className="md:col-span-2">
                      <Label className="text-xs text-gray-500">Observações</Label>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selected.notes}</p>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4">
                  <Label className="text-xs text-gray-500 mb-2 block">Projetos vinculados</Label>
                  {(projectsByClient[selected.user_id] || []).length === 0 ? (
                    <p className="text-sm text-gray-500 italic">Nenhum projeto vinculado ainda</p>
                  ) : (
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {(projectsByClient[selected.user_id] || []).map(p => (
                        <div key={p.project_id} className="flex items-center gap-2 text-sm py-1.5 px-2 bg-gray-50 rounded">
                          {p.project_number && (
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                              {p.project_number}
                            </span>
                          )}
                          <span className="truncate flex-1">{p.name}</span>
                          <Badge className="text-[10px] bg-gray-200 text-gray-800">{p.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <DialogFooter className="gap-2">
                  {user?.role?.toUpperCase() === 'ADMIN' && (
                    <Button variant="outline" onClick={() => openEdit(selected)} data-testid="btn-edit-client">
                      Editar
                    </Button>
                  )}
                  <MonthlyReportButton
                    clientId={selected.user_id}
                    clientName={selected.full_name || selected.email}
                  />
                  <Button onClick={() => setSelected(null)}>Fechar</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit modal */}
        <Dialog open={!!editForm} onOpenChange={(o) => !o && setEditForm(null)}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Editar Cliente</DialogTitle>
              <DialogDescription className="sr-only">
                Formulário de edição de dados do cliente
              </DialogDescription>
            </DialogHeader>
            {editForm && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                <div className="md:col-span-2">
                  <Label>Tipo</Label>
                  <Select
                    value={editForm.person_type}
                    onValueChange={(v) => setEditForm({ ...editForm, person_type: v })}
                  >
                    <SelectTrigger data-testid="edit-person-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PF">Pessoa Física</SelectItem>
                      <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>{editForm.person_type === 'PJ' ? 'Razão Social *' : 'Nome Completo *'}</Label>
                  <Input
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                    data-testid="edit-full-name"
                  />
                </div>
                {editForm.person_type === 'PJ' && (
                  <>
                    <div>
                      <Label>Nome Fantasia</Label>
                      <Input
                        value={editForm.company_name}
                        onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Pessoa de Contato</Label>
                      <Input
                        value={editForm.contact_person}
                        onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })}
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label>E-mail</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Observações</Label>
                  <Input
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditForm(null)}>Cancelar</Button>
              <Button onClick={saveEdit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-save-client">
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <NewClientDialog
          open={newClientOpen}
          onOpenChange={setNewClientOpen}
          onCreated={fetchAll}
        />
        <MergeClientsDialog
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          onMerged={fetchAll}
        />
      </div>
    </Layout>
  );
}

export default Clients;
